import { useEffect } from 'react'
import { useParams } from 'react-router'
import { Button, Grid, IconButton, useMediaQuery } from '@material-ui/core'
import { InsertDriveFileOutlined } from '@material-ui/icons'

import { UserIcon } from 'asterX-UIkit/Icons'
import { useAuthStore } from 'context/AuthStore'
import { useHomeStore } from 'context/HomeStore'
import { useResErrors } from 'hooks'
import { MiscService } from 'lib/api'
import { bytesToMB } from 'lib/utils'
import moment from 'moment'
import { getFileName } from 'constants/constants'
import { useChatMessageStyles } from './chatMessage.styles'

const Message = ({ isCurrentUser, classes, message }) => {
  return (
    <Grid
      item
      xs={12}
      sm={12}
      className={`${
        isCurrentUser ? classes.primaryBackground : classes.secondaryBackground
      }`}
    >
      <p className={classes.messageText}>{message}</p>
    </Grid>
  )
}
const FileMessage = ({ handleDownload, classes, url, size, fileName }) => {
  return (
    <Grid
      item
      container
      alignItems="center"
      xs={12}
      md={12}
      className={`${classes.fileBackground}`}
    >
      <div style={{ maxWidth: '20%' }}>
        <IconButton onClick={() => handleDownload(url)}>
          <InsertDriveFileOutlined />
        </IconButton>
      </div>
      <div style={{ maxWidth: '80%' }}>
        <p className={classes.originalFileName}>{fileName}</p>
        <p className={classes.size}>{bytesToMB(size)}</p>
      </div>
    </Grid>
  )
}
const ReasonMessage = ({ setOpenRenewModal, status, classes, reason }) => {
  return (
    <Grid
      item
      container
      xs={12}
      md={12}
      direction="column"
      className={`${classes.errorBackground}`}
    >
      <div style={{ marginBottom: 8 }}>
        <p className={classes.errorTitle}>Отказан</p>
      </div>
      <div style={{ marginBottom: 8 }}>
        <p className={classes.messageText}>{reason}</p>
      </div>
      <div>
        <p className={classes.messageText} style={{ marginBottom: 8 }}>
          При необходимости вы можете возобновить заявку
        </p>
        <Button
          fullWidth
          variant="outlined"
          color="default"
          disabled={['В работе'].includes(status)}
          onClick={() => setOpenRenewModal(prev => !prev)}
          style={{ height: 45 }}
        >
          Возобновить заявку
        </Button>
      </div>
    </Grid>
  )
}
const CompleteMessage = ({
  setOpenReviewModal,
  hadQuestionnaire,
  handleDownload,
  files,
  classes,
  complete
}) => {
  return (
    <Grid
      item
      container
      xs={12}
      md={12}
      direction="column"
      className={`${classes.completeBackground}`}
    >
      <div style={{ marginBottom: 8 }}>
        <p className={classes.errorTitle}>Успешно завершена</p>
      </div>
      <div style={{ marginBottom: 8 }}>
        <p className={classes.messageText}>{complete}</p>
      </div>
      {(files || []).map((url, idx) => (
        <div key={`files_${idx}`} className={classes.completeContainer}>
          <div style={{ maxWidth: '20%' }}>
            <IconButton onClick={() => handleDownload(url)}>
              <InsertDriveFileOutlined />
            </IconButton>
          </div>
          <div style={{ maxWidth: '80%' }}>
            <p className={classes.messageText}>{getFileName(url)}</p>
          </div>
        </div>
      ))}
      <Button
        fullWidth
        variant="outlined"
        color="primary"
        disabled={hadQuestionnaire}
        onClick={() => setOpenReviewModal(prev => !prev)}
        className={classes.completeButton}
      >
        Оставить отзыв
      </Button>
    </Grid>
  )
}

export const ChatMessage = () => {
  const { id } = useParams()
  const { standardCheck } = useResErrors()
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'))
  const classes = useChatMessageStyles()
  const {
    homeStore: { chat, requestList },
    homeStoreFunc: {
      setChat,
      setRequestList,
      setOpenRenewModal,
      setOpenReviewModal
    }
  } = useHomeStore()

  const {
    authStore: { user }
  } = useAuthStore()

  useEffect(() => {
    if (requestList.status !== 'Завершен') {
      const interval = setInterval(() => {
        getChat(id)
      }, 20000)

      return () => clearInterval(interval)
    }
  }, [id, requestList.status])

  const handleDownload = url => {
    window.open(url, '_blank')
  }

  const getChat = async id => {
    if (id) {
      await MiscService.getSupportById(id).then(res =>
        standardCheck(
          res,
          ({ data }) => {
            setRequestList(data)
            let newChat = data.chat ? JSON.parse(data.chat) : []
            if (data.reason && ['Отказан', 'Отменен'].includes(data.status)) {
              newChat.push({
                reason: data.reason,
                author: data.adminUsername,
                fullName: data.adminFullname,
                date: data.completedDate
              })
            }
            setChat(newChat)
          },
          {
            onFail: () => {
              setChat([])
            }
          }
        )
      )
    }
  }

  return chat.map((item, index, array) => {
    const isCurrentUser = item?.author === user.data.username
    const current = moment(item?.date).format('DD.MM.YYYY')
    const prevDate =
      index > 0 ? moment(array[index - 1].date).format('DD.MM.YYYY') : null
    const date = prevDate && prevDate !== current ? prevDate : current

    return (
      <Grid
        item
        container
        direction="column"
        xs={12}
        md={12}
        style={{
          width: 'fit-content',
          marginLeft: isCurrentUser ? 'auto' : ''
        }}
      >
        <Grid item container xs={12} md={12}>
          {!isCurrentUser && (
            <Grid item style={{ marginRight: 10 }}>
              <IconButton className={classes.iconButton}>
                <UserIcon />
              </IconButton>
            </Grid>
          )}
          <Grid
            item
            container
            style={{
              height: 'fit-content',
              width: 'fit-content',
              maxWidth: isMobile ? 270 : 320,
              marginBottom: 15
            }}
          >
            <Grid
              item
              container
              xs={12}
              md={12}
              justifyContent="space-between"
              style={{ marginBottom: 5 }}
            >
              <span className={classes.messageAuthor}>
                {isCurrentUser ? 'Вы' : item.fullName}
              </span>
              <span className={classes.messageTime}>
                {`${
                  date === moment().format('DD.MM.YYYY') ? 'Сегодня' : date
                } ${moment(item?.date).format('HH:mm')}`}
              </span>
            </Grid>
            {item?.message && (
              <Message
                isCurrentUser={isCurrentUser}
                classes={classes}
                message={item?.message}
              />
            )}
            {item?.file && (
              <FileMessage
                handleDownload={handleDownload}
                classes={classes}
                url={item?.file?.fileDownloadUri}
                size={item?.file?.size}
                fileName={item?.file?.originalFileName}
              />
            )}
            {item?.reason && (
              <ReasonMessage
                setOpenRenewModal={setOpenRenewModal}
                status={requestList?.status}
                classes={classes}
                reason={item?.reason}
              />
            )}
            {item?.complete && (
              <CompleteMessage
                setOpenReviewModal={setOpenReviewModal}
                hadQuestionnaire={requestList?.hadQuestionnaire}
                handleDownload={handleDownload}
                files={item?.files}
                classes={classes}
                complete={item?.complete}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    )
  })
}
